/**
 * Field/operator registry for MedDream hangingProtocolsV2 (v8.9.0).
 *
 * Single source of truth for the authoring UI's dropdowns, the condition DSL
 * parser, and both rule engines.
 *
 * Transcribed from MedDream's own `GET /hanging-protocol/configuration`, which
 * is what its settings screen loads the dropdowns from. That endpoint — not the
 * rendered UI and not the exported file — is authoritative, and it corrected
 * three things an earlier pass had inferred:
 *
 *  1. `operations` is per FIELD, not per input type. `sop_instance_UID` allows
 *     only contain/not_contain; `scroll_step` only `=`; `study_date_filter`
 *     only `<=`. Offering all six would produce protocols MedDream rejects.
 *  2. There is a third input type, `datetime`.
 *  3. Prior and Other study conditions draw from *different* vocabularies
 *     (`modalities_in_patient_study` vs `modalities_in_open_study`), so they are
 *     separate scopes rather than one shared "prior" scope.
 *
 * `path` is also served per field, so it is a property of the field after all —
 * with the caveat that the exported file carries per-rule variants for
 * `series.ViewPosition[ANY]` and `series.Laterality[All]`. Imported rules keep
 * whatever path they arrived with; these values apply only to NEW rules.
 *
 * Field names are reproduced verbatim, including MedDream's own typo in
 * `instance.SamplesPerPixel[0028,0002)]` — changing it would break matching.
 *
 * Order within each scope mirrors the endpoint, so our dropdowns read like
 * MedDream's.
 */

export const INPUT_TYPE = {
  STRING: "string",
  NUMERIC: "numeric",
  DATETIME: "datetime",
};

/**
 * The full operator sets, used as a fallback for fields we have no definition
 * for. Prefer {@link getOperators}, which returns the field's real set.
 */
export const OPERATORS_BY_INPUT_TYPE = {
  [INPUT_TYPE.STRING]: ["match", "not_match", "begins_with", "end_with", "contain", "not_contain"],
  [INPUT_TYPE.NUMERIC]: ["=", ">", "<", ">=", "<=", "!="],
  [INPUT_TYPE.DATETIME]: ["<="],
};

export const ALL_OPERATORS = Array.from(
  new Set([
    ...OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.STRING],
    ...OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.NUMERIC],
    ...OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.DATETIME],
  ])
);

/* ------------------------------------------------------- display labels */

/**
 * Human labels for the stored operator tokens.
 *
 * The wire format keeps the tokens — these exist only so the authoring UI stops
 * showing `not_contain` and `end_with` to a radiographer. Never persist these.
 */
export const OPERATOR_LABELS = {
  match: "is",
  not_match: "is not",
  contain: "contains",
  not_contain: "does not contain",
  begins_with: "starts with",
  end_with: "ends with",
  "=": "=",
  "!=": "is not",
  ">": "greater than",
  "<": "less than",
  ">=": "at least",
  "<=": "at most",
};

/**
 * `<=` is the only operator `study_date_filter` allows, and "at most" reads as
 * nonsense against a date.
 */
const DATETIME_OPERATOR_LABELS = { "<=": "on or before" };

export function getOperatorLabel(operator, inputType) {
  if (inputType === INPUT_TYPE.DATETIME && DATETIME_OPERATOR_LABELS[operator]) {
    return DATETIME_OPERATOR_LABELS[operator];
  }
  return OPERATOR_LABELS[operator] || operator;
}

/** Field names the prettifier below cannot derive a good label for. */
const FIELD_LABEL_OVERRIDES = {
  main_study_modality: "Modality",
  sop_instance_UID: "SOP instance UID",
  study_date_filter: "Study date",
  scroll_step: "Prior study offset",
};

/** `study.ManufacturerModelName[0008,1090]` -> scope, name, tag. */
const DICOM_FIELD = /^(?:study|series|instance)\.([A-Za-z]+)\[(.+)]$/;

const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/** Words that are already an acronym keep their case: SOP, UID, PET. */
const isAcronym = (word) => /[A-Z]/.test(word) && word === word.toUpperCase();

/**
 * A readable label for a field name, for dropdowns and the summary line.
 *
 * The raw name stays the source of truth and is shown alongside the label in the
 * picker — admins who know the format by name still need to find it.
 */
export function getFieldLabel(field) {
  if (!field) return "";
  if (FIELD_LABEL_OVERRIDES[field]) return FIELD_LABEL_OVERRIDES[field];

  const dicom = DICOM_FIELD.exec(field);
  if (dicom) {
    const words = dicom[1].replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
    return `${capitalise(words)} [${dicom[2]}]`;
  }

  const words = field.split("_").filter(Boolean);
  if (words.length === 0) return field;
  return words
    .map((word, index) => (isAcronym(word) ? word : index === 0 ? capitalise(word) : word.toLowerCase()))
    .join(" ");
}

/** Serialised into `rules` as infix separators; the UI presents them as && / ||. */
export const COMBINATOR = {
  AND: "and",
  OR: "or",
};

export const COMBINATOR_TO_DSL = {
  [COMBINATOR.AND]: "&&",
  [COMBINATOR.OR]: "||",
};

export const DSL_TO_COMBINATOR = {
  "&&": COMBINATOR.AND,
  "||": COMBINATOR.OR,
};

export const CONDITION_SCOPE = {
  GROUP: "group",
  STUDY: "study",
  SERIES: "series",
  INSTANCE: "instance",
  PRIOR: "prior",
  OTHER: "other",
};

/** Which vocabulary each condition slot in the document draws from. */
export const SLOT_SCOPE = {
  groupConditions: CONDITION_SCOPE.GROUP,
  studyConditions: CONDITION_SCOPE.STUDY,
  seriesConditions: CONDITION_SCOPE.SERIES,
  petSeriesConditions: CONDITION_SCOPE.SERIES,
  instanceConditions: CONDITION_SCOPE.INSTANCE,
  koInstance: CONDITION_SCOPE.INSTANCE,
  autoApplyPR: CONDITION_SCOPE.INSTANCE,
  priorStudyConditions: CONDITION_SCOPE.PRIOR,
  otherStudyConditions: CONDITION_SCOPE.OTHER,
};

/** MedDream's endpoint key for each scope, for regenerating this file. */
export const SCOPE_ENDPOINT_KEY = {
  [CONDITION_SCOPE.GROUP]: "groupConditions",
  [CONDITION_SCOPE.STUDY]: "studyConditions",
  [CONDITION_SCOPE.PRIOR]: "priorStudyConditions",
  [CONDITION_SCOPE.OTHER]: "otherStudyConditions",
  [CONDITION_SCOPE.SERIES]: "viewportSeriesConditions",
  [CONDITION_SCOPE.INSTANCE]: "viewportInstanceConditions",
};

/* ------------------------------------------------------------ shorthands */

const S = INPUT_TYPE.STRING;
const N = INPUT_TYPE.NUMERIC;
const D = INPUT_TYPE.DATETIME;

/** The six string operators, in MedDream's order. */
const STR6 = OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.STRING];
/** The six numeric operators, in MedDream's order. */
const NUM6 = OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.NUMERIC];
/** Sequence-valued attributes only support containment. */
const CONTAIN2 = ["contain", "not_contain"];

const PATH_VIEW_CODE_SEQUENCE = [
  "attribute.00540220",
  "attribute.00540220/00080121",
  "attribute.00540220/00540222",
  "attribute.00540220/00540222/00080121",
];
const PATH_VIEW_POSITION_ANY = [
  "viewPosition",
  "protocolName",
  "attribute.0008103E",
  ...PATH_VIEW_CODE_SEQUENCE,
];
const PATH_LATERALITY_X = ["imageLaterality", "attribute.00200060"];
const PATH_LATERALITY_ALL = [...PATH_LATERALITY_X, "frameLaterality"];
const PATH_ACQ_DEVICE_PROC_DESC = [
  "attribute.00181400",
  "attribute.00189506/00181400",
  "attribute.52009229/00189412/00181400",
  "attribute.52009230/00189412/00181400",
];

const f = (name, inputType, operations, path = []) => ({ name, inputType, operations, path });

/* ---------------------------------------------------------- definitions */

/** Group conditions — 18 fields. */
const GROUP_DEFS = [
  f("main_study_modality", S, STR6),
  f("modalities_in_study", S, STR6),
  f("open_studies_count", N, NUM6),
  f("patient_studies_count", N, NUM6),
  f("patient_studies_modality_count", N, NUM6),
  f("open_studies_modality_count", N, NUM6),
  f("series_count", N, NUM6),
  f("study_description", S, STR6),
  f("study_instances_count", N, NUM6),
  f("study.DeviceSerialNumber[0018,1000]", S, STR6, ["attribute.00081000", "attribute.00500010/00081000"]),
  f("study.Manufacturer[0008,0070]", S, STR6, ["attribute.00080070", "attribute.00500010/00080070"]),
  f("study.ManufacturerModelName[0008,1090]", S, STR6, ["attribute.00081090", "attribute.00500010/00081090"]),
  f("study.StationName[0008,1010]", S, STR6, ["attribute.00081010"]),
  f("series_description", S, STR6),
  f("series.ViewCodeSequence[0054,0220]", S, CONTAIN2, PATH_VIEW_CODE_SEQUENCE),
  f("series.ViewPosition[0018,5101]", S, STR6, ["viewPosition"]),
  f("series_SOP_class_UID", S, STR6),
  f("sop_instance_UID", S, CONTAIN2),
];

/** Study conditions — 25 fields. */
const STUDY_DEFS = [
  f("main_study_modality", S, STR6),
  f("modalities_in_study", S, STR6),
  f("open_studies_count", N, NUM6),
  f("patient_studies_count", N, NUM6),
  f("patient_studies_modality_count", N, NUM6),
  f("open_studies_modality_count", N, NUM6),
  f("series_count", N, NUM6),
  f("series_description", S, STR6),
  f("series_SOP_class_UID", S, STR6),
  f("series_transfer_syntax_UID", S, STR6),
  f("study_description", S, STR6),
  f("study_instances_count", N, NUM6),
  f("study.AcquisitionDeviceProcessingDescription[0018,1400]", S, STR6, PATH_ACQ_DEVICE_PROC_DESC),
  f("study.DeviceSerialNumber[0018,1000]", S, STR6, ["attribute.00081000", "attribute.00500010/00081000"]),
  f("study.InstitutionName[0008,0080]", S, STR6, ["attribute.00080080"]),
  f("study.Manufacturer[0008,0070]", S, STR6, ["attribute.00080070", "attribute.00500010/00080070"]),
  f("study.ManufacturerModelName[0008,1090]", S, STR6, ["attribute.00081090", "attribute.00500010/00081090"]),
  f("study.StationName[0008,1010]", S, STR6, ["attribute.00081010"]),
  f("series.FrameLaterality[0020,9072]", S, STR6, ["frameLaterality"]),
  f("series.ImageLaterality[0020,0062]", S, STR6, ["imageLaterality"]),
  f("series.Laterality[0020,006X]", S, STR6, PATH_LATERALITY_X),
  f("series.ViewCodeSequence[0054,0220]", S, CONTAIN2, PATH_VIEW_CODE_SEQUENCE),
  f("series.Laterality[All]", S, STR6, PATH_LATERALITY_ALL),
  f("series.ViewPosition[ANY]", S, STR6, PATH_VIEW_POSITION_ANY),
  f("sop_instance_UID", S, CONTAIN2),
];

/** Prior study conditions — 4 fields. */
const PRIOR_DEFS = [
  f("modalities_in_patient_study", S, STR6),
  f("patient_study_description", S, STR6),
  f("scroll_step", N, ["="]),
  f("study_date_filter", D, ["<="]),
];

/** Other study conditions — 4 fields, a different vocabulary to PRIOR. */
const OTHER_DEFS = [
  f("modalities_in_open_study", S, STR6),
  f("open_study_description", S, STR6),
  f("scroll_step", N, ["="]),
  f("study_date_filter", D, ["<="]),
];

/** Series + PET series conditions — 27 fields. */
const SERIES_DEFS = [
  f("matched_series_number", N, NUM6),
  f("scroll_matched_series", N, ["="]),
  f("series_description", S, STR6),
  f("series_instances_count", N, NUM6),
  f("series_modality", S, STR6),
  f("series_number", N, NUM6),
  f("series_SOP_class_UID", S, STR6),
  f("series_transfer_syntax_UID", S, STR6),
  f("series.AcquisitionDeviceProcessingDescription[0018,1400]", S, STR6, PATH_ACQ_DEVICE_PROC_DESC),
  f("series.AnatomicRegionSequence[0008,2218]", S, STR6, ["anatomicRegion"]),
  f("series.BodyPartExamined[0018,0015]", S, STR6, ["bodyPartExamined"]),
  f("series.BreastImplantPresent[0028,1300]", S, STR6, ["attribute.00281300"]),
  f("series.ContrastBolusAgent[0018,0010]", S, STR6, ["attribute.00180010"]),
  f("series.FrameLaterality[0020,9072]", S, STR6, ["frameLaterality"]),
  f("series.ImageLaterality[0020,0062]", S, STR6, ["imageLaterality"]),
  f("series.Laterality[0020,006X]", S, STR6, PATH_LATERALITY_X),
  f("series.ImageType[0008,0008]", S, STR6, ["imageType"]),
  f("series.PatientOrientation[0020,0020]", S, STR6, ["patientOrientation"]),
  f("series.PrimaryAnatomicStructureSequence[0008,2228]", S, CONTAIN2, ["attribute.00082228"]),
  f("series.ProtocolName[0018,1030]", S, STR6, ["protocolName"]),
  f("series.RotationDirection[0018,1140]", S, STR6, ["rotationDirection"]),
  f("series.SeriesNumber[0020,0011]", N, NUM6, ["seriesNumber"]),
  f("series.ViewCodeSequence[0054,0220]", S, CONTAIN2, PATH_VIEW_CODE_SEQUENCE),
  f("series.ViewPosition[0018,5101]", S, STR6, ["viewPosition"]),
  f("series.Laterality[All]", S, STR6, PATH_LATERALITY_ALL),
  f("series.ViewPosition[ANY]", S, STR6, PATH_VIEW_POSITION_ANY),
  f("sop_instance_UID", S, CONTAIN2),
];

/** Instance / KO / auto-apply-PR conditions — 23 fields. */
const INSTANCE_DEFS = [
  f("instance_number", N, NUM6),
  f("instance_SOP_class_UID", S, STR6),
  f("instance_transfer_syntax_UID", S, STR6),
  f("number_of_frames", N, NUM6),
  f("instance.AcquisitionDeviceProcessingDescription[0018,1400]", S, STR6, PATH_ACQ_DEVICE_PROC_DESC),
  f("instance.AnatomicRegionSequence[0008,2218]", S, STR6, ["anatomicRegion"]),
  f("instance.BodyPartExamined[0018,0015]", S, STR6, ["bodyPartExamined"]),
  f("instance.BreastImplantPresent[0028,1300]", S, STR6, ["attribute.00281300"]),
  f("instance.ContrastBolusAgent[0018,0010]", S, STR6, ["attribute.00180010"]),
  f("instance.ImageLaterality[0020,0062]", S, STR6, ["imageLaterality"]),
  f("instance.Laterality[0020,006X]", S, STR6, PATH_LATERALITY_X),
  f("instance.ImageType[0008,0008]", S, STR6, ["imageType"]),
  f("instance.PatientOrientation[0020,0020]", S, STR6, ["patientOrientation"]),
  f("instance.PrimaryAnatomicStructureSequence[0008,2228]", S, CONTAIN2, ["attribute.00082228"]),
  f("instance.ProtocolName[0018,1030]", S, STR6, ["protocolName"]),
  f("instance.RotationDirection[0018,1140]", S, STR6, ["rotationDirection"]),
  f("instance.SamplesPerPixel[0028,0002)]", N, NUM6, ["attribute.00280002"]),
  f("instance.ViewCodeSequence[0054,0220]", S, CONTAIN2, PATH_VIEW_CODE_SEQUENCE),
  f("instance.ViewPosition[0018,5101]", S, STR6, ["viewPosition"]),
  f("matched_instance_number", N, NUM6),
  f("scroll_matched_instances", N, ["="]),
  f("instance.Laterality[All]", S, STR6, PATH_LATERALITY_ALL),
  f("instance.ViewPosition[ANY]", S, STR6, PATH_VIEW_POSITION_ANY),
];

export const FIELD_DEFS_BY_SCOPE = {
  [CONDITION_SCOPE.GROUP]: GROUP_DEFS,
  [CONDITION_SCOPE.STUDY]: STUDY_DEFS,
  [CONDITION_SCOPE.PRIOR]: PRIOR_DEFS,
  [CONDITION_SCOPE.OTHER]: OTHER_DEFS,
  [CONDITION_SCOPE.SERIES]: SERIES_DEFS,
  [CONDITION_SCOPE.INSTANCE]: INSTANCE_DEFS,
};

export const FIELDS_BY_SCOPE = Object.fromEntries(
  Object.entries(FIELD_DEFS_BY_SCOPE).map(([scope, defs]) => [scope, defs.map((d) => d.name)])
);

/**
 * Field name -> definition. The same field appears in several scopes with an
 * identical definition, so first-wins is safe; `assertRegistryConsistent`
 * guards that assumption.
 */
const FIELD_BY_NAME = new Map();
for (const defs of Object.values(FIELD_DEFS_BY_SCOPE)) {
  for (const def of defs) {
    if (!FIELD_BY_NAME.has(def.name)) FIELD_BY_NAME.set(def.name, def);
  }
}

/**
 * Superset used for validation and DSL tokenising. Kept wider than any single
 * dropdown because the exported file uses fields across scope boundaries — e.g.
 * `series_number` appears inside instanceConditions.
 */
export const ALL_KNOWN_FIELDS = Array.from(FIELD_BY_NAME.keys());

/**
 * `path` values applied when creating a NEW rule. Imported rules keep the path
 * they arrived with: MedDream's export carries two orderings for
 * `series.ViewPosition[ANY]` and two lengths for `series.Laterality[All]`.
 */
export const DEFAULT_RULE_PATHS = Object.fromEntries(
  Array.from(FIELD_BY_NAME.values())
    .filter((d) => d.path.length > 0)
    .map((d) => [d.name, d.path])
);

/* ------------------------------------------------------------- accessors */

export function getFieldDef(field) {
  return FIELD_BY_NAME.get(field) || null;
}

export function getInputType(field) {
  return FIELD_BY_NAME.get(field)?.inputType ?? INPUT_TYPE.STRING;
}

/** The operators MedDream actually offers for this field — not all six. */
export function getOperators(field) {
  const def = FIELD_BY_NAME.get(field);
  if (def) return def.operations;
  return OPERATORS_BY_INPUT_TYPE[INPUT_TYPE.STRING];
}

export function isOperatorAllowed(field, operator) {
  return getOperators(field).includes(operator);
}

export function getFieldsForSlot(slot) {
  return FIELDS_BY_SCOPE[SLOT_SCOPE[slot]] || [];
}

export function getFieldDefsForSlot(slot) {
  return FIELD_DEFS_BY_SCOPE[SLOT_SCOPE[slot]] || [];
}

export function getDefaultPath(field) {
  const def = FIELD_BY_NAME.get(field);
  return def ? [...def.path] : [];
}

export function isKnownField(field) {
  return FIELD_BY_NAME.has(field);
}

/**
 * Guards the first-wins assumption above: a field appearing in two scopes must
 * carry the same definition in both. Exercised by the registry test.
 */
export function assertRegistryConsistent() {
  const conflicts = [];
  const seen = new Map();
  for (const [scope, defs] of Object.entries(FIELD_DEFS_BY_SCOPE)) {
    for (const def of defs) {
      const previous = seen.get(def.name);
      if (!previous) {
        seen.set(def.name, { scope, def });
        continue;
      }
      const same =
        previous.def.inputType === def.inputType &&
        previous.def.operations.join(",") === def.operations.join(",") &&
        previous.def.path.join(",") === def.path.join(",");
      if (!same) {
        conflicts.push(`${def.name}: ${previous.scope} vs ${scope}`);
      }
    }
  }
  return conflicts;
}
